import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, ShoppingBag, Trash2 } from 'lucide-react';
import { useCart } from '@/context/CartContext';

export default function CartSidebar({ onCheckout }) {
  const { items, updateQty, removeItem, total, count, open, setOpen } = useCart();

  useEffect(() => {
    if (open) {
      const handleEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [open, setOpen]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[80]" onClick={() => setOpen(false)} />
          <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md z-[81] flex flex-col"
            style={{ backgroundColor: 'var(--color-bg)' }} data-testid="cart-sidebar">
            
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <h2 className="flex items-center gap-2 text-xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                <ShoppingBag size={20} style={{ color: 'var(--color-primary)' }} />
                Votre panier ({count})
              </h2>
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-[var(--color-muted)] transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag size={48} className="mx-auto mb-4" style={{ color: 'var(--color-muted-fg)' }} />
                  <p style={{ color: 'var(--color-muted-fg)' }}>Votre panier est vide</p>
                </div>
              ) : items.map(item => (
                <div key={item.key} className="flex gap-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
                  {item.image && <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{item.name}</p>
                    <p className="text-xs" style={{ color: 'var(--color-muted-fg)' }}>{item.format}</p>
                    <p className="text-sm font-bold mt-1" style={{ color: 'var(--color-primary)' }}>{(item.price * item.qty).toFixed(2)} $</p>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button onClick={() => removeItem(item.key)} className="text-red-400/60 hover:text-red-400"><Trash2 size={14} /></button>
                    <div className="flex items-center gap-2 mt-1">
                      <button onClick={() => updateQty(item.key, item.qty - 1)}
                        className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold"
                        style={{ backgroundColor: 'var(--color-border)' }}>
                        <Minus size={12} />
                      </button>
                      <span className="text-sm font-bold w-5 text-center">{item.qty}</span>
                      <button onClick={() => updateQty(item.key, item.qty + 1)}
                        className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-white"
                        style={{ backgroundColor: 'var(--color-primary)' }}>
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="p-5 border-t space-y-4" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span style={{ color: 'var(--color-primary)' }}>{total.toFixed(2)} $</span>
                </div>
                <button onClick={() => { setOpen(false); onCheckout?.(); }}
                  data-testid="cart-checkout-btn"
                  className="w-full py-3.5 rounded-xl text-white font-semibold text-base transition-all hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg, #F78A7D, #E8665A)' }}>
                  Passer la commande
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
